import { useState, useEffect } from "react";
import { Plus, Trash2, Package, Wrench, Send, Search } from "lucide-react";
import { calcLine, inr } from "../lib";
import { api } from "@/lib/api";

/**
 * Reusable editable line-item table used by three sections:
 *   • Parts
 *   • Labour Charges
 *   • Other Charges
 *
 * The `kind` prop selects the fields:
 *   parts → sku, name, description, qty, unit, unit_price, discount, tax_pct, optional
 *   labor → task_name, description, qty, unit_price, discount, tax_pct
 *   other → charge_type, description, qty, unit_price, discount, tax_pct
 */
export function LineItemTable({ kind, items, onChange, taxMode = "exclusive" }) {
  const [pickerOpen, setPickerOpen] = useState(null); // {index}
  const cfg = KIND_CONFIG[kind];

  const addRow = () => {
    const row = { ...cfg.emptyRow, id: crypto.randomUUID?.() || String(Date.now()) };
    onChange([...items, row]);
  };

  const patchRow = (i, patch) => {
    const next = items.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    onChange(next);
  };

  const removeRow = (i) => {
    onChange(items.filter((_, idx) => idx !== i));
  };

  return (
    <section
      className="bg-white rounded-xl border border-[#E4E7EC] overflow-hidden"
      data-testid={`est-${kind}-section`}
    >
      <header className="px-4 py-3 border-b border-[#F1FAEE] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-md ${cfg.chip} grid place-items-center`}>
            <cfg.Icon size={14} />
          </div>
          <div>
            <h3 className="text-[14px] font-semibold text-[#1d3557]">{cfg.title}</h3>
            <p className="text-[10.5px] text-[#667085]">{cfg.subtitle}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={addRow}
          data-testid={`est-${kind}-add`}
          className="h-8 px-3 rounded-md border border-[#E4E7EC] bg-white text-[12px] font-semibold text-[#1d3557] hover:border-[#e63946] hover:text-[#e63946] inline-flex items-center gap-1"
        >
          <Plus size={12} /> Add {cfg.singular}
        </button>
      </header>

      {items.length === 0 ? (
        <div className="p-6 text-center">
          <div className="text-[12.5px] text-[#667085]">
            No {cfg.title.toLowerCase()} yet.{" "}
            <button
              type="button"
              onClick={addRow}
              className="text-[#e63946] font-semibold hover:underline"
            >
              Add the first {cfg.singular.toLowerCase()}
            </button>
            .
          </div>
        </div>
      ) : (
        <>
          {/* Desktop */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-[12.5px] border-separate" style={{ borderSpacing: 0 }}>
              <thead>
                <tr className="text-[10px] uppercase tracking-wider font-semibold text-[#667085] bg-[#F7F9FC]">
                  <th className="px-3 py-2.5 text-left min-w-[260px] border-b border-[#F1FAEE]">{cfg.nameLabel}</th>
                  <th className="px-2 py-2.5 text-center w-[80px] border-b border-[#F1FAEE]">Qty</th>
                  <th className="px-2 py-2.5 text-right w-[130px] border-b border-[#F1FAEE]">Rate (₹)</th>
                  <th className="px-2 py-2.5 text-right w-[70px] border-b border-[#F1FAEE]">Disc %</th>
                  <th className="px-2 py-2.5 text-center w-[80px] border-b border-[#F1FAEE]">Tax</th>
                  <th className="px-3 py-2.5 text-right w-[120px] border-b border-[#F1FAEE]">Amount</th>
                  <th className="w-[50px] border-b border-[#F1FAEE]"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((r, i) => {
                  const c = calcLine(r, taxMode);
                  return (
                    <tr
                      key={r.id || i}
                      className="hover:bg-[#F7F9FC]/60 transition-colors"
                      data-testid={`est-${kind}-row-${i}`}
                    >
                      {/* Name + description */}
                      <td className="px-3 py-2 align-top border-b border-[#F1FAEE]">
                        <div className="flex items-start gap-1.5">
                          {(kind === "parts" || kind === "labor") && (
                            <button
                              type="button"
                              onClick={() => setPickerOpen({ index: i, kind })}
                              className="mt-1.5 text-[#a8dadc] hover:text-[#457b9d]"
                              title={kind === "parts" ? "Pick from inventory" : "Pick from service tasks"}
                              data-testid={`est-${kind}-pick-${i}`}
                            >
                              <Search size={12} />
                            </button>
                          )}
                          <div className="flex-1 min-w-0">
                            <input
                              type="text"
                              value={r[cfg.nameKey] || ""}
                              onChange={(e) => patchRow(i, { [cfg.nameKey]: e.target.value })}
                              placeholder={cfg.namePlaceholder}
                              data-testid={`est-${kind}-name-${i}`}
                              className="w-full h-8 px-1.5 text-[13px] font-medium text-[#1d3557] bg-transparent focus:bg-white focus:border focus:border-[#457b9d] rounded outline-none"
                            />
                            <input
                              type="text"
                              value={r.description || ""}
                              onChange={(e) => patchRow(i, { description: e.target.value })}
                              placeholder="Description (optional)"
                              className="w-full h-6 px-1.5 text-[11px] text-[#667085] bg-transparent focus:bg-white focus:border focus:border-[#a8dadc] rounded outline-none"
                            />
                          </div>
                        </div>
                      </td>

                      {/* Qty — dropdown 1..10 */}
                      <td className="px-2 py-2 align-top border-b border-[#F1FAEE]">
                        <select
                          value={Number(r.qty) >= 1 && Number(r.qty) <= 10 ? Number(r.qty) : 1}
                          onChange={(e) => patchRow(i, { qty: Number(e.target.value) })}
                          data-testid={`est-${kind}-qty-${i}`}
                          className="w-full h-8 px-2 text-center font-mono text-[13px] tabular-nums rounded-md border border-[#E4E7EC] bg-white focus:border-[#457b9d] outline-none appearance-none cursor-pointer"
                        >
                          {Array.from({ length: 10 }, (_, n) => n + 1).map((n) => (
                            <option key={n} value={n}>{n}</option>
                          ))}
                        </select>
                      </td>

                      {/* Rate */}
                      <td className="px-2 py-2 align-top border-b border-[#F1FAEE]">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={r.unit_price ?? 0}
                          onChange={(e) => patchRow(i, { unit_price: Number(e.target.value) })}
                          data-testid={`est-${kind}-rate-${i}`}
                          className="w-full h-8 px-2 text-right font-mono text-[13px] tabular-nums rounded-md border border-[#E4E7EC] bg-white focus:border-[#457b9d] outline-none"
                        />
                      </td>

                      {/* Discount — % only, no toggle */}
                      <td className="px-2 py-2 align-top border-b border-[#F1FAEE]">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={r.discount_value ?? 0}
                          onChange={(e) => patchRow(i, { discount_value: Number(e.target.value), discount_type: "percent" })}
                          data-testid={`est-${kind}-disc-${i}`}
                          className="w-full h-8 px-2 text-right font-mono text-[13px] tabular-nums rounded-md border border-[#E4E7EC] bg-white focus:border-[#457b9d] outline-none"
                        />
                      </td>

                      {/* Tax */}
                      <td className="px-2 py-2 align-top border-b border-[#F1FAEE]">
                        <select
                          value={r.tax_pct ?? 18}
                          onChange={(e) => patchRow(i, { tax_pct: Number(e.target.value) })}
                          data-testid={`est-${kind}-tax-${i}`}
                          className="w-full h-8 px-2 text-center text-[13px] font-mono tabular-nums rounded-md border border-[#E4E7EC] bg-white focus:border-[#457b9d] outline-none appearance-none cursor-pointer"
                        >
                          <option value={0}>0%</option>
                          <option value={5}>5%</option>
                          <option value={18}>18%</option>
                        </select>
                      </td>

                      {/* Amount */}
                      <td className="px-3 py-2 text-right align-top border-b border-[#F1FAEE]">
                        <div className="h-8 flex items-center justify-end font-mono font-semibold text-[13.5px] text-[#1d3557] tabular-nums">
                          {inr(c.line_total)}
                        </div>
                      </td>

                      {/* Row actions — remove only */}
                      <td className="px-2 py-2 align-top border-b border-[#F1FAEE]">
                        <div className="h-8 flex items-center justify-end">
                          <button
                            type="button"
                            onClick={() => removeRow(i)}
                            title="Remove"
                            data-testid={`est-${kind}-remove-${i}`}
                            className="p-1 text-[#a8dadc] hover:text-[#e63946]"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile — stacked cards */}
          <div className="md:hidden divide-y divide-[#F1FAEE]">
            {items.map((r, i) => {
              const c = calcLine(r, taxMode);
              return (
                <div key={r.id || i} className="p-3">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <input
                        type="text"
                        value={r[cfg.nameKey] || ""}
                        onChange={(e) => patchRow(i, { [cfg.nameKey]: e.target.value })}
                        placeholder={cfg.namePlaceholder}
                        data-testid={`est-${kind}-name-m-${i}`}
                        className="w-full h-8 text-[13.5px] font-semibold text-[#1d3557] bg-transparent border-b border-[#E4E7EC] focus:border-[#457b9d] outline-none"
                      />
                      <input
                        type="text"
                        value={r.description || ""}
                        onChange={(e) => patchRow(i, { description: e.target.value })}
                        placeholder="Description"
                        className="w-full h-7 text-[11.5px] text-[#667085] bg-transparent outline-none"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeRow(i)}
                      className="text-[#a8dadc] hover:text-[#e63946] p-1"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    <label className="block">
                      <span className="text-[9.5px] uppercase tracking-wider font-semibold text-[#667085]">Qty</span>
                      <select
                        value={Number(r.qty) >= 1 && Number(r.qty) <= 10 ? Number(r.qty) : 1}
                        onChange={(e) => patchRow(i, { qty: Number(e.target.value) })}
                        className="mt-1 w-full h-8 px-1.5 text-center font-mono text-[13px] rounded border border-[#E4E7EC] focus:border-[#457b9d] outline-none bg-white cursor-pointer"
                      >
                        {Array.from({ length: 10 }, (_, n) => n + 1).map((n) => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    </label>
                    <MobileNum label="Rate" value={r.unit_price} onChange={(v) => patchRow(i, { unit_price: Number(v) })} />
                    <div className="text-right">
                      <div className="text-[9.5px] uppercase tracking-wider font-semibold text-[#667085]">Total</div>
                      <div className="text-[13.5px] font-mono font-semibold text-[#1d3557] mt-1">{inr(c.line_total)}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {pickerOpen && (
        <PickerDialog
          kind={pickerOpen.kind}
          onClose={() => setPickerOpen(null)}
          onPick={(picked) => {
            patchRow(pickerOpen.index, picked);
            setPickerOpen(null);
          }}
        />
      )}
    </section>
  );
}

const KIND_CONFIG = {
  parts: {
    title: "Parts", subtitle: "Spare parts required for this repair.",
    singular: "Part", nameLabel: "Part", nameKey: "name", namePlaceholder: "Part name…",
    Icon: Package, chip: "bg-[#e63946]/10 text-[#e63946]",
    emptyRow: {
      name: "", sku: "", description: "", qty: 1, unit_price: 0,
      discount_type: "percent", discount_value: 0, tax_pct: 18, unit: "nos",
    },
  },
  labor: {
    title: "Labour Charges", subtitle: "Time and skill needed by the technician.",
    singular: "Labour", nameLabel: "Task", nameKey: "task_name", namePlaceholder: "Task name…",
    Icon: Wrench, chip: "bg-[#457b9d]/10 text-[#457b9d]",
    emptyRow: {
      task_name: "", description: "", qty: 1, unit_price: 500,
      discount_type: "percent", discount_value: 0, tax_pct: 18,
    },
  },
  other: {
    title: "Other Charges", subtitle: "Consumables, pickup, workshop supplies, misc.",
    singular: "Charge", nameLabel: "Charge type", nameKey: "charge_type", namePlaceholder: "Consumables, pickup, etc…",
    Icon: Send, chip: "bg-[#F79009]/10 text-[#F79009]",
    emptyRow: {
      charge_type: "", description: "", qty: 1, unit_price: 0,
      discount_type: "percent", discount_value: 0, tax_pct: 18,
    },
  },
};

const MobileNum = ({ label, value, onChange }) => (
  <label className="block">
    <span className="text-[9.5px] uppercase tracking-wider font-semibold text-[#667085]">{label}</span>
    <input
      type="number"
      step="0.01"
      value={value ?? 0}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full h-8 px-1.5 text-right font-mono text-[13px] rounded border border-[#E4E7EC] focus:border-[#457b9d] outline-none tabular-nums"
    />
  </label>
);

function PickerDialog({ kind, onClose, onPick }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    if (kind === "parts") {
      api.get("/parts", { params: q ? { q } : {} })
        .then((r) => setItems(r.data || []))
        .finally(() => setLoading(false));
    } else {
      api.get("/master/service-tasks-v2")
        .then((r) => {
          const list = r.data || [];
          setItems(q ? list.filter((t) => (t.name || "").toLowerCase().includes(q.toLowerCase())) : list);
        })
        .finally(() => setLoading(false));
    }
  }, [q, kind]);
  const applyPick = (it) => {
    if (kind === "parts") {
      onPick({
        part_id: it.id, sku: it.sku, name: it.name,
        unit_price: it.price || 0, tax_pct: 18, unit: it.unit || "nos",
        description: it.description || "",
      });
    } else {
      onPick({
        task_id: it.id, task_name: it.name,
        qty: it.hours_est || 1, unit_price: it.labor_rate || 500, tax_pct: 18,
      });
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl w-full max-w-md max-h-[70vh] flex flex-col shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-[#F1FAEE] flex items-center gap-2">
          <Search size={14} className="text-[#a8dadc]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
            placeholder={kind === "parts" ? "Search inventory / SKU…" : "Search service task…"}
            className="flex-1 h-8 outline-none text-[13px]"
            data-testid={`est-picker-search-${kind}`}
          />
          <button onClick={onClose} className="text-[#a8dadc] hover:text-[#e63946]">✕</button>
        </div>
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="p-6 text-center text-[12px] text-[#667085]">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-6 text-center text-[12px] text-[#667085]">No matches.</div>
          ) : (
            <ul>
              {items.slice(0, 100).map((it) => (
                <li key={it.id || it.sku || it.name}>
                  <button
                    type="button"
                    onClick={() => applyPick(it)}
                    data-testid={`est-picker-item-${kind}`}
                    className="w-full text-left p-3 border-b border-[#F1FAEE] hover:bg-[#F7F9FC]"
                  >
                    <div className="text-[13px] font-semibold text-[#1d3557]">
                      {it.name}
                    </div>
                    <div className="text-[11px] text-[#667085] flex items-center gap-2">
                      {it.sku && <span className="font-mono">{it.sku}</span>}
                      {kind === "parts" ? (
                        <>
                          <span>Stock: {it.stock_qty ?? 0}</span>
                          <span className="font-mono">{inr(it.price)}</span>
                        </>
                      ) : (
                        <>
                          <span>{it.hours_est} hr</span>
                          <span className="font-mono">{inr(it.labor_rate)}</span>
                        </>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
